import { AxiosError } from "axios"
import { Grid, Link, TextField } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import React, { useState } from 'react'
import { EventCustomPopup, EventPopupGrid } from './components'
import { EventTypes, PopupFields, PopupFieldsProps } from './types'
import Paragraph from '../Paragraph/index'
import StatusLabel from '../StatusLabel'
import palette from '../../../theme/palette'
import { setActiveRoute } from '../../../redux/reducers/appSettingsReducer'
import { useAppDispatch } from '../../../redux/store'
import { StyledLink } from '../Link/components'
import Modal from "../Modal"
import { availabilityService } from "../../../services/availabilityService"
import { v2MyActionsService } from "../../../services/myActionsServiceV2"
import { isEnhancement } from "../../../utils/SharedMethods/isEnhancement"
import { useDefaultErrorHandler } from "../../../utils/Hooks/useDefaultErrorHandler"
import { showSuccessMessage } from "../../../redux/reducers/snackbarReducer"
import { BaseResponse } from "../../../types/base-response"

export function EventPopup(props: PopupFieldsProps) {
  const [currentLoc] = useState<string>(window.location.pathname)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteRequestId, setDeleteRequestId] = useState<number | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteComment, setDeleteComment] = useState('')
  const [showCommentBox, setShowCommentBox] = useState<boolean>(false)
  const [cancellationRequested, setCancellationRequested] = useState<boolean>(false)
  const [eventStatuses] = useState<{ [key: number]: string }>({})
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const defaultErrorHandler = useDefaultErrorHandler()

  const onNavigateURLSwitch = (id: number, absenceType: string) => {
    let url
    const normalise = (type: string) => type.replace(/ /g, '').toLowerCase()
    switch (normalise(absenceType)) {
      case normalise(palette.type.absence.label):
        url = 'absenceoccurrence'
        navigate(`/${props.baseUrl}/${url}/${id}`, {
          state: { previousLocation: `/${props.baseUrl}`, calendar: true },
        })
        break
      case normalise(palette.type.dayOff.label):
        url = 'dayoffrequest'
        break
      case normalise(palette.type.holiday.label):
        url = 'holidayrequest'
        break
      case normalise(palette.type.holidayM.label):
        url = 'manualholidayrequest'
        break
      case normalise(palette.type.late.label):
        url = 'lateoccurrence'
        navigate(`/${props.baseUrl}/${url}/${id}`, {
          state: { previousLocation: `/${props.baseUrl}`, calendar: true },
        })
        break
      case normalise(palette.type.lieuDay.label):
      case normalise(palette.type.lieu.label):
        url = 'lieurequest'
        break
      case normalise(palette.type.lieuM.label):
        url = 'manuallieurequest'
        break
      case normalise(palette.type.workFromHome.type):
        url = 'workfromhomerequest'
        break
      case normalise(palette.type.shift.label):
        url = 'shiftrequest'
        break
      case normalise(palette.type.maternityLeave.label):
        url = 'maternityrequest'
        break
      case normalise(palette.type.paternityLeave.label):
        url = 'paternityrequest'
        break
      case normalise(palette.type.twilightCover.type):
        url = 'twilightshiftcoverrequest'
        break
      case normalise(palette.type.nightShiftCover.label):
        url = 'nightshiftcoverrequest'
        break
      case normalise(palette.type.course.label):
        url = 'courserequest'
        break
      case normalise(palette.type.adjustment.label):
        url = 'adjustmentrequest'
        break
      case normalise(palette.type.birthday.label):
        url = 'birthdayrequest'
        break
      case normalise(palette.type.night.label):
        url = 'nightrequest'
        break
      case normalise(palette.type.overtime.label):
        url = 'overtimerequest'
        break
      case normalise(palette.type.onCall.label):
        url = 'oncallrequest'
        break
      case normalise(palette.type.callOut.label):
        url = 'calloutrequest'
        break
      case normalise(palette.type.weekend.label):
        url = 'weekendrequest'
        break
      default:
        url = 'otherrequest'
    }
    const activeLocation: string = currentLoc.split('/').filter(x => x !== '')[0]
    navigate(`/${activeLocation}/${url}/${id}`, {
      state: { previousLocation: `/${activeLocation}`, calendar: true },
    })
    dispatch(setActiveRoute(`/${activeLocation}`))
  }

  const displayDrawer = (eventType: string, isFromComingUp: boolean | undefined): boolean => {
    const type = eventType.toLowerCase()
    return (type !== 'absence' && type !== 'late') &&
        (location.pathname.length === 1 || location.pathname.includes('myavailability') ||
            (location.pathname.includes('dashboard') && Boolean(isFromComingUp)))
  }

  const onNavigate = (
    id: number,
    eventType: string,
    eventStatus: string | undefined,
    isFromComingUp: boolean | undefined,
    requestType?: string | undefined
  ) => {
    if (currentLoc === '/dashboard' && eventType === EventTypes.ABSENCE) {
      navigate(`/myavailability/absenceoccurrence/${id}`, {
        state: { previousLocation: '/dashboard', calendar: false },
      })
      return
    }
    if (displayDrawer(eventType, isFromComingUp)) {
      props.onDrawerOpen?.(id, eventType, eventStatus, requestType)
      return
    }
    onNavigateURLSwitch(id, eventType)
  }

  const popUpStatusLabel = (pu: PopupFields) => eventStatuses[pu.id] || pu.eventStatus

  const hoursText = (puHours: number, eventType: string) => {
    if (!puHours || eventType.toLowerCase() === 'late') return ''
    return `${puHours.toFixed(2)} Hours`
  }

  const cancelChangeRequest = async (id: number) => {
    await availabilityService.cancelChangeRequest({
      requestId: id,
      requestStatusId: 2,
    })
    dispatch(showSuccessMessage('Change request has been cancelled'))
  }

  const cancelCancellationRequest = async (id: number) => {
    await availabilityService.cancelChangeRequest({
      requestId: id,
      requestStatusId: 2,
    })
    dispatch(showSuccessMessage('Cancellation request has been cancelled'))
  }
  const cancelApprovedRequest = async (id: number) => {
    await availabilityService.cancelApprovedRequest({
      requestId: id,
      cancelComments: deleteComment,
    })
    dispatch(showSuccessMessage('Cancel request has been sent'))
  }

  const deleteRequest = async (id: number) => {
    const request = isEnhancement(props.fields[0].eventType)
        ? v2MyActionsService.deleteEnhancement(id)
        : availabilityService.deleteMyRequest(id)
    await request
    dispatch(showSuccessMessage('Request has been deleted'))
  }

  const isApprovedOrAmended = (status?: string): boolean => {
    const statusLower = status?.toLowerCase();
    return statusLower === 'approved' || statusLower === 'amended';
  }

  const handleDelete = async (id: number | null, pu: PopupFields) => {
    if (id === null) return

    if (showCommentBox && !deleteComment) {
      return
    }

    setDeleteLoading(true)
    try {
      if (cancellationRequested) {
        await cancelCancellationRequest(id)
      } else if (pu.eventStatus?.toLowerCase() === 'change requested') {
        await cancelChangeRequest(id)
      } else if (!isEnhancement(pu.eventType) && isApprovedOrAmended(pu.eventStatus)) {
        await cancelApprovedRequest(id)
      } else {
        await deleteRequest(id)
      }
      props.onClose?.()
    } catch (err) {
      defaultErrorHandler(err as AxiosError<BaseResponse>)
    } finally {
      setDeleteLoading(false)
      setShowDeleteModal(false)
      props.onUpdateRequest?.(id, pu?.requestType || '')
    }
  }  
  const confirmDelete = (id: number, pu: PopupFields) => {
    const isApproved = isApprovedOrAmended(pu.eventStatus)
    setDeleteRequestId(id)
    setShowDeleteModal(true)
    setShowCommentBox(isApproved)
    setCancellationRequested(pu.eventStatus?.toLowerCase() === 'cancellation requested')
    setDeleteComment('')
  }

 
  const showDeleteButton = (eventType: string, requestType?: string, status?: string) => {
    if (props.hideDeleteButton) {
      return false
    }

    const type = eventType.toLowerCase()
    const isHolidayManualRequest = requestType === "Holiday (M)"
    const isLieuManualRequest = requestType === "Lieu (M)"
    const isStatusApproved = status?.toLowerCase() === 'approved'
    const isStatusPending = status?.toLowerCase() === 'pending'
    const isAbsenceOrLate = type === 'absence' || type === 'late'
    const isEnhancementNotPending = isEnhancement(eventType) && !isStatusPending

    if (isHolidayManualRequest && !isStatusPending) {
      return false
    }

    if (isLieuManualRequest && isStatusApproved) {
      return false
    }

    return !(isEnhancementNotPending || isAbsenceOrLate)
  }
  
  const selectedPu = props.fields.find(field => field.id === deleteRequestId)
  const renderDropdown = () => {
    if (!showCommentBox) return undefined
    
    return (
      <TextField
        label="Comments"
        value={deleteComment}
        fullWidth
        multiline
        rows={4}
        onChange={e => setDeleteComment(e.target.value)}
        error={!deleteComment}
        helperText={!deleteComment ? "Required" : ""}
      />
    )
  }
  
  return (
    <>
      <EventCustomPopup
        open={props.fields[0].open}
        anchorEl={props.fields[0].anchorEl}
        placement={props.fields[0].placement}
      >
        <EventPopupGrid
          container
          width={`${231 * props.fields.length}px`}
          multiEvent={props.fields.length > 1}
        >
          {props.fields.map((pu, idx) => (
            <Grid
              key={pu.id}
              item
              xs={12 / props.fields.length}
              borderRight={idx < props.fields.length - 1 ? `1px solid #f1f1f1` : 'unset'}
            >
              <Grid container m={0}>
                <Grid item xs={12} mb="8px">
                  <Paragraph color="black" weight="bold">
                    {pu.eventType.toLowerCase() === 'on call'
                      ? 'On Call'
                      : `${hoursText(pu.hours, pu.eventType)} ${pu.eventType}`}
                  </Paragraph>
                </Grid>
                {pu.eventStatus?.toLowerCase() !== 'unspecified' && (
                  <Grid item xs display="flex" justifyContent="center">
                    <StatusLabel status={popUpStatusLabel(pu)} />
                  </Grid>
                )}
                {Boolean(pu.id) && !pu.disableEdit && !props.isAttendanceCalendar && (
                  <Grid item xs={12} display="flex" justifyContent="center" mt={2}>
                    <Link component="button" variant="body2">
                      <StyledLink
                        onClick={() => {
                          onNavigate(
                            pu.id,
                            pu.eventType,
                            pu.eventStatus,
                            props.isComingUp,
                            pu.requestType
                          )
                        }}
                        data-testid={`view-edit-${pu.id}`}
                      >
                        View/Edit
                      </StyledLink>
                    </Link>
                  </Grid>
                )}
                {showDeleteButton(pu.eventType,pu.requestType, pu.eventStatus) && (
                  <Grid item xs={12} bgcolor="white" textAlign="center" mt={1}>
                    <Link component="button" variant="body2" ml={2}>
                      <StyledLink onClick={() => confirmDelete(pu.id, pu)} data-testid={`delete-${pu.id}`}>
                        Delete
                      </StyledLink>
                    </Link>
                  </Grid>
                )}
              </Grid>
            </Grid>
          ))}
          <Grid item xs={12} bgcolor="white" textAlign="center" mt={1}>
            <Link onClick={props.onClose} component="button" variant="body2">
              <StyledLink>Close</StyledLink>
            </Link>
          </Grid>
        </EventPopupGrid>
      </EventCustomPopup>

      <Modal
        type="question"
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title='Are you sure?'
        message='Are you sure you would like to cancel this request?'
        buttonLabel='Cancel Request'
        onClick={() => handleDelete(deleteRequestId, selectedPu!)}
        buttonLoading={deleteLoading}
        dropdown={renderDropdown()}
      />
    </>
  )
}